import type { ContractItemsQueryParams, ContractItemsResponse } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils';

import {
  zodContractItemsQueryParamsSchema,
  zodContractItemsResponseSchema,
} from './validations';

/**
 * Fetches all line items for a contract from the API with the given query parameters.
 *
 * @param contractId - The UUID of the contract to fetch items for.
 * @param queryParams - The query parameters for pagination.
 * @param options - Additional request options.
 * @returns The paginated contract items data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/list_contract_items_contracts__contract_id__items_get
 */
export const fetchContractItems = async (
  contractId: UUID,
  queryParams: Partial<ContractItemsQueryParams>,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.CONTRACT_ITEM.GET_ALL(contractId), {
      searchParams: queryParams,
      validate: {
        searchParams: zodContractItemsQueryParamsSchema.transform((data) =>
          withoutObjectKeysWhereValueIs(data, [undefined, '', false]),
        ),
      },
      ...options,
    })
    .json<ContractItemsResponse | undefined>();

  // Validate the response with Zod
  const validatedResponse = zodContractItemsResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Contract Items API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
